// src/components/EvaluationConfigurationStepOne.js - ORIGINAL SOLO SIN TIMELIMIT
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Work as WorkIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/system';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

// Componentes estilizados
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.08) : 'white',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: selected ? `0 0 0 1px ${theme.palette.primary.main}` : 'none',
  '&:hover': {
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.12) : '#f5f5f5',
    borderColor: selected ? theme.palette.primary.main : theme.palette.primary.light,
    transform: 'translateY(-2px)',
    boxShadow: selected
      ? `0 4px 8px rgba(0,0,0,0.1), 0 0 0 1px ${theme.palette.primary.main}`
      : '0 2px 4px rgba(0,0,0,0.1)',
  },
}));

const EvaluationConfigurationStepOne = ({
  selectedPosition,
  onPositionSelect,
  evaluationData,
  onEvaluationChange
}) => {
  // Estados
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState(null);

  const { authToken, showAlert } = useStore.getState();
  const { t } = useTranslation();

  // Manejar cambios en los datos del evaluation
  const handleEvaluationDataChange = (field, value) => {
    const newData = { ...evaluationData, [field]: value };
    onEvaluationChange(newData);
  };

  // API Functions
  const getAllPositions = async () => {
    const { project } = useStore.getState();
    const response = await api.get("/positions", {
      params: { projectId: project },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const loadPositions = async () => {
    try {
      setLoading(true);
      const response = await getAllPositions();
      if (response.response && response.response.status_response === 'OK') {
        setPositions(response.response.data || []);
        setFilteredPositions(response.response.data || []);
        setError(null);
      } else {
        setError(t('positions.loadError'));
        showAlert(t('positions.loadError'), "warning");
      }
    } catch (err) {
      setError(t('generalApiError'));
      showAlert(t('generalApiError'), "error");
      console.error('Error loading positions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar cargos al montar el componente
  useEffect(() => {
    loadPositions();
  }, []);

  // Función para filtrar cargos por búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredPositions(positions);
    } else {
      const filtered = positions.filter(position =>
        position.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        position.industria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        position.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPositions(filtered);
    }
  }, [searchTerm, positions]);

  // Actualizar título automáticamente cuando se selecciona un cargo
  useEffect(() => {
    if (selectedPosition && (!evaluationData?.title || evaluationData.title === '')) {
      handleEvaluationDataChange('title', `${t('evaluation')} - ${selectedPosition.name}`);
    }
  }, [selectedPosition]);

  // Función para formatear fechas
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';

    try {
      let date;

      if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else if (dateValue && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        date = new Date(dateValue);
      }

      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return t('dateNotAvailable');
    }
  };

  // Función para manejar la selección de cargo
  const handlePositionSelect = (position) => {
    onPositionSelect(position);
  };

  // Función para abrir modal de descripción
  const handleOpenModal = (position, event) => {
    event.stopPropagation(); // Evitar que se seleccione la tarjeta
    setModalPosition(position);
    setModalOpen(true);
  };

  // Función para cerrar modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalPosition(null);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <WorkIcon sx={{ mr: 1, color: 'primary.main' }} />
        {t('evaluations.configurationTitle')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('evaluations.configurationStepOneSubTitle')}
      </Typography>

      {/* Barra de búsqueda */}
      <TextField
        fullWidth
        placeholder={t('positions.searchPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadPositions}>
              <RefreshIcon sx={{ mr: 1 }} />
              {t('retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Lista de cargos */}
      {!loading && !error && (
        <>
          {filteredPositions.length === 0 ? (
            <Alert severity="info">
              {searchTerm ?
                t('positions.noPositionsFound') + ` "${searchTerm}"` :
                t('positions.noPositions')
              }
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 430, overflowY: 'auto', pr: 1 }}>
              <Grid container spacing={2}>
                {filteredPositions.map((position) => {
                  const isSelected = selectedPosition && selectedPosition.uid === position.uid;

                  return (
                    <Grid item xs={12} md={6} key={position.id || position.uid}>
                      <StyledCard
                        selected={isSelected}
                        onClick={() => handlePositionSelect(position)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                {position.name}
                              </Typography>

                              <Chip
                                label={position.industria}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mb: 1 }}
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 1,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {position.descripcion ?
                                  position.descripcion.substring(0, 150) + "..."
                                  : position.descripcion || t('noDescription')}
                              </Typography>

                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {t('created')}: {formatDate(position.created)}
                                </Typography>

                                {position.descripcion && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleOpenModal(position, e)}
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </StyledCard>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Configuración de la Evaluación (solo se muestra si hay un cargo seleccionado) */}
      {selectedPosition && (
        <>
          <Divider sx={{ my: 4 }} />

          <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
              {t('evaluations.configurationStepOneFormTitle')}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('evaluations.configurationStepOneFormSubTitle')} <strong>{selectedPosition.name}</strong>.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label={`${t('evaluations.configurationStepOneFormTitleLabel')}`}
                value={evaluationData?.title || ''}
                onChange={(e) => handleEvaluationDataChange('title', e.target.value)}
                fullWidth
                required
                placeholder={`${t('evaluations.configurationStepOneFormTitlePlaceholder')} - ${selectedPosition.name}`}
                helperText={`${t('evaluations.configurationStepOneFormTitleHelper')} *`}
              />

              <TextField
                label={`${t('evaluations.configurationStepOneFormDescriptionLabel')}`}
                value={evaluationData?.description || ''}
                onChange={(e) => handleEvaluationDataChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={`${t('evaluations.configurationStepOneFormDescriptionPlaceholder')}`}
                helperText={`${t('evaluations.configurationStepOneFormDescriptionHelper')}`}
              />


              {/* Resumen del cargo seleccionado */}
              <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  {t('reports.layout.selectedPosition')}:
                </Typography>
                <Typography variant="body2">
                  <strong>{selectedPosition.name}</strong> - {selectedPosition.industria}
                </Typography>
                {selectedPosition.descripcion && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedPosition.descripcion.length > 200
                      ? selectedPosition.descripcion.substring(0, 200) + "..."
                      : selectedPosition.descripcion}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {/* Modal para mostrar descripción completa */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">{modalPosition?.name}</Typography>
            <Chip
              label={modalPosition?.industria}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </Box>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {modalPosition?.descripcion || t('noDescription')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal}>
            {t('close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handlePositionSelect(modalPosition);
              handleCloseModal();
            }}
          >
            {t('evaluations.selectPosition')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationConfigurationStepOne;